import { Trans, useTranslation } from "react-i18next";

import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";

import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { Info } from "../../../components/Info";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { AtiraEmpty } from "../../../components/AtiraEmpty";
import { entryActions } from "../../../redux/entry/entry.slice";
import styled from "styled-components";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { Spacing } from "../../../theme/Spacing";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Regexes } from "../../../utils/String";
import { faPhone } from "@fortawesome/free-solid-svg-icons/faPhone";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons/faCircleInfo";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons/faMagnifyingGlass";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import React, { useMemo, useState } from "react";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Link } from "react-router-dom";
import { SettingsSubRoutes } from "../../../model/app/types/SettingsSubRoutes.enum";
import { Tooltip } from "../../../utils/local-antd";
import { AtiraToast } from "../../../utils/AtiraToast";
import { useFormContext } from "react-hook-form";
import { GetLatestEntriesFilterDto } from "../../../model/entry/dto/GetLatestEntriesFilterDto";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import { useDrawersContext } from "../../tasks/drawers-context";
import { Entry } from "../../../model/entry/Entry";
import { EntryKind } from "../../../model/entry/types/EntryKind.enum";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardsGrid,
} from "./DuplicateCardPrimitives";
import { EntryKindBadge } from "./EntryKindBadge";
import { TestIDs } from "../../../tests/types/testIds";

const CardTitle = styled(TextWithTruncated)`
  font-weight: bold;
  direction: ltr;
`;

const MatchedByRow = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xs};
`;

const KindMixRow = styled(Flex)`
  flex-wrap: wrap;
  gap: ${Spacing.s};
`;

const BannerRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.m};
  padding: ${Spacing.l} ${Spacing.l} 0;
`;

const Toolbar = styled(Flex)`
  position: sticky;
  top: 0;
  z-index: 1;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.m};
  padding: ${Spacing.m} ${Spacing.l};
  background-color: ${({ theme }) => theme.white};
`;

const GridArea = styled.div`
  padding: 0 ${Spacing.l} ${Spacing.l};
`;

const SearchWrapper = styled.div`
  width: min(100%, 26rem);
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.main};
  text-decoration: underline;
  text-underline-offset: 5px;
  transition: all 0.3s;

  &:hover {
    color: ${({ theme }) => theme.lightMain};
  }
`;

const countByKind = (entries: Entry[]) =>
  entries.reduce<Partial<Record<EntryKind, number>>>((acc, entry) => {
    acc[entry.kind] = (acc[entry.kind] || 0) + 1;
    return acc;
  }, {});

const onlyDigits = (value: string) => value.replace(/\D/g, "");

export const EntryDuplicateBulkInitiateResolveDrawer: React.FC = () => {
  const [ignoreButtonLoading, setIgnoreButtonLoading] = useState(false);
  const [query, setQuery] = useState("");

  const { t } = useTranslation();
  const {
    duplicateEntryInitiateResolveVisible,
    setDuplicateEntryInitiateResolveVisible,
    setDuplicateEntryVisible,
    setDuplicateEntries,
    currentOnIgnoreAction,
    duplicateEntriesGroups,
  } = useDrawersContext();
  const dispatch = useAppDispatch();
  const { getValues } = useFormContext<GetLatestEntriesFilterDto>();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const isOrgMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);

  const groups = useMemo(
    () =>
      Object.entries(duplicateEntriesGroups || {}).map(([key, data]) => ({
        key,
        data,
        isEmail: Regexes.EMAIL_REGEX.test(key),
        title:
          data.find((entry) => entry.fields?.name)?.fields?.name ||
          t("entries.findDuplicates.card.unnamed"),
        kindCounts: countByKind(data),
      })),
    [duplicateEntriesGroups, t],
  );

  const normalizedQuery = query.trim().toLowerCase();
  const queryDigits = onlyDigits(normalizedQuery);

  const visibleGroups = groups.filter((group) => {
    if (!normalizedQuery) {
      return true;
    }

    const kinds = Object.keys(group.kindCounts).map((kind) =>
      t(`common.${kind.toLowerCase()}`),
    );
    const haystack = [group.title, group.key, ...kinds].join(" ").toLowerCase();

    return (
      haystack.includes(normalizedQuery) ||
      (queryDigits.length >= 3 && onlyDigits(group.key).includes(queryDigits))
    );
  });

  const localOnClose = () => {
    setDuplicateEntryInitiateResolveVisible(false);
    setDuplicateEntries([]);
  };

  // Opens exactly the group the card shows. Re-checking one of its entries on
  // the server would match on its email *or* phone and pull in other groups.
  const onExploreDuplicates = (key: string) => {
    const groupEntries = duplicateEntriesGroups?.[key];

    if (!groupEntries?.length) {
      return;
    }

    setDuplicateEntries(groupEntries);
    setDuplicateEntryVisible(true);
  };

  const onIgnoreAndContinue = async () => {
    try {
      setIgnoreButtonLoading(true);
      await currentOnIgnoreAction?.();

      await dispatch(
        entryActions.getLatestEntries({
          userId,
          ...{
            ...omitBy(getValues(), isNil),
            assigneeIds: getValues("assigneeIds")?.filter(Boolean),
          },
          meta: {
            count: 20,
            page: 0,
          },
        }),
      ).unwrap();
      localOnClose();
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setIgnoreButtonLoading(false);
    }
  };

  return (
    <Drawer
      title={t("entries.findDuplicates.drawer.title")}
      onClose={localOnClose}
      open={duplicateEntryInitiateResolveVisible}
      width={"100%"}
      destroyOnHidden
      styles={{ body: { padding: 0 } }}
      extra={
        currentOnIgnoreAction ? (
          <Tooltip title={t("entries.duplicates.actions.ignore.tooltip")}>
            <Button
              backgroundColor="danger"
              margin="none"
              onClick={onIgnoreAndContinue}
              loading={ignoreButtonLoading}
            >
              {t("entries.duplicates.actions.ignore")}
            </Button>
          </Tooltip>
        ) : null
      }
    >
      <Flex flexDirection="column">
        <BannerRow>
          <Info info={t("entries.findDuplicates.info-1")} width="100%" />

          <Tooltip
            title={
              <Flex flexDirection="column" gap="s">
                <Text fontSize="xs" color="white">
                  <Trans
                    i18nKey={`entries.findDuplicates.info-2${!isOrgMember ? "-with-link" : ""}`}
                    components={[
                      <StyledLink
                        to={`/settings/${SettingsSubRoutes.FIELDS}`}
                      />,
                    ]}
                  />
                </Text>
                <Text fontSize="xs" color="white">
                  {t("entries.findDuplicates.info-3")}
                </Text>
              </Flex>
            }
          >
            <span>
              <AtiraIcon icon={faCircleInfo} color="darkerSub" size="lg" />
            </span>
          </Tooltip>
        </BannerRow>

        <Toolbar>
          <SearchWrapper>
            <Input
              icon={faMagnifyingGlass}
              iconSize="1x"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("entries.findDuplicates.search.placeholder")}
            />
          </SearchWrapper>

          <Text fontSize="s" color="darkerSub">
            {t("entries.findDuplicates.search.count", {
              shown: visibleGroups.length,
              total: groups.length,
            })}
          </Text>
        </Toolbar>

        <GridArea>
          {visibleGroups.length ? (
            <CardsGrid $equalRows>
              {visibleGroups.map(
                ({ key, data, isEmail, title, kindCounts }) => (
                  <Card
                    key={key}
                    data-testid={TestIDs.entries.duplicates.picker.card(key)}
                  >
                    <CardHeader>
                      <Flex flexDirection="column" gap="xs" minWidth={0}>
                        <CardTitle length={Lengths.NAME} cutText>
                          {title}
                        </CardTitle>

                        <MatchedByRow>
                          <AtiraIcon
                            icon={isEmail ? faEnvelope : faPhone}
                            color="darkerSub"
                            size="xs"
                          />
                          <Text fontSize="xs" color="darkerSub">
                            {t("entries.findDuplicates.card.matchedBy")}:
                          </Text>
                          <TextWithTruncated
                            length={Lengths.NAME}
                            fontSize="xs"
                            color="darkerSub"
                            direction="ltr"
                          >
                            {key}
                          </TextWithTruncated>
                        </MatchedByRow>
                      </Flex>
                    </CardHeader>

                    <CardBody>
                      <Flex alignItems="baseline" gap="s">
                        <Text fontSize="l" fontWeight="bold" color="main">
                          <span
                            data-testid={TestIDs.entries.duplicates.picker.count(
                              key,
                            )}
                          >
                            {data.length}
                          </span>
                        </Text>
                        <Text fontSize="s" color="darkerSub">
                          {t("entries.findDuplicates.card.count.title")}
                        </Text>
                      </Flex>

                      <KindMixRow>
                        {Object.entries(kindCounts).map(([kind, count]) => (
                          <EntryKindBadge
                            key={kind}
                            kind={kind as EntryKind}
                            count={count}
                          />
                        ))}
                      </KindMixRow>
                    </CardBody>

                    <CardFooter>
                      <Button
                        onClick={() => onExploreDuplicates(key)}
                        dataTestId={TestIDs.entries.duplicates.picker.explore(
                          key,
                        )}
                        icon={faMagnifyingGlass}
                        width="100%"
                        margin="none"
                        hover
                      >
                        {t("entries.findDuplicates.card.action")}
                      </Button>
                    </CardFooter>
                  </Card>
                ),
              )}
            </CardsGrid>
          ) : (
            <AtiraEmpty
              icon={faMagnifyingGlass}
              iconHeight="4rem"
              description={t("entries.findDuplicates.search.empty")}
            />
          )}
        </GridArea>
      </Flex>
    </Drawer>
  );
};
